import React, { FC, useState, useRef, ReactNode } from 'react'
import { Close } from '@mui/icons-material'
import cn from '../../lib/utils/cn'
import dayjs from 'dayjs'
import { useClickOutside } from '@mantine/hooks'
import Loader from '../common/Loader'

interface ItemMultiValueInput {
	id: string
	label: string
}

interface MultiValueInputProps {
	value?: string[]
	onChange?: (value: string[]) => void
	onItemClick?: (item: ItemMultiValueInput) => void
	onDeleteItem?: (value: string) => void
	label?: ReactNode
	placeholder?: string
	error?: string
	wrapperClassName?: string
	labelClassName?: string
	selectClassName?: string
	itemsWrapperClassName?: string
	itemClassName?: string
	valueClassName?: string
	className?: string
	disabled?: boolean
	type?: 'text' | 'date'
	items?: ItemMultiValueInput[]
	withoutKeyConfirm?: boolean
	loading?: boolean
}

const MultiValueInput: FC<MultiValueInputProps> = ({
	value = [],
	onChange,
	onItemClick,
	onDeleteItem,
	label = null,
	placeholder = 'Add values',
	error,
	wrapperClassName = '',
	labelClassName = '',
	selectClassName = '',
	itemsWrapperClassName = '',
	itemClassName = '',
	valueClassName = '',
	className = '',
	disabled = false,
	type = 'text',
	items = [],
	withoutKeyConfirm = false,
	loading = false,
}) => {
	const [inputValue, setInputValue] = useState('')
	const [isOpen, setIsOpen] = useState(false)
	const inputRef = useRef<HTMLInputElement>(null)
	const wrapperRef = useClickOutside(() => setIsOpen(false))

	const filteredItems = items?.filter(
		(item) =>
			item?.label?.toLowerCase()?.includes(inputValue?.toLowerCase()) &&
			!value?.includes(item.id)
	)

	const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
		if (e.key === 'Enter' && inputValue?.trim()) {
			e.preventDefault()
			if (!value.includes(inputValue?.trim()) && !!onChange) {
				onChange?.([...value, inputValue?.trim()])
			}
			setInputValue('')
		}
	}

	const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const date = e.target.value
		if (date && !value.includes(date) && !!onChange) {
			onChange([...value, date])
		}
		setInputValue('')
	}

	const handleRemoveValue = (valueToRemove: string, e: React.MouseEvent) => {
		e.stopPropagation()
		if (onDeleteItem) {
			onDeleteItem?.(valueToRemove)
			return
		}
		onChange?.(value.filter((v) => v !== valueToRemove))
	}

	const handleItemClick = (item: ItemMultiValueInput) => {
		onItemClick?.(item)
		setInputValue('')
		setIsOpen(false)
		inputRef?.current?.blur()
	}

	// useEffect(() => {
	// 	setIsOpen(Boolean(!!inputValue && items?.length))
	// }, [inputValue, filteredItems])

	return (
		<div
			className={cn(
				'flex flex-col w-full relative',
				disabled && 'opacity-60 pointer-events-none',
				wrapperClassName
			)}
		>
			{label && (
				<label
					className={cn(
						'text-text-main dark:text-text-main-dark font-semibold text-base mb-2',
						labelClassName
					)}
				>
					{label}
				</label>
			)}
			<div className={cn('relative w-full', className)} ref={wrapperRef}>
				<div
					className={cn(
						'min-h-[36px] px-2 py-1 border border-stroke-dark dark:border-stroke-light-dark bg-white dark:bg-background-dark rounded-md overflow-x-hidden',
						'flex flex-wrap items-center gap-1',
						error && 'border-error',
						selectClassName,
						isOpen && filteredItems?.length && 'rounded-b-none'
					)}
				>
					{loading && value?.length ? (
						<div className="w-full h-8 flex items-center justify-center">
							<Loader size={15} className="dark:text-text-main-dark" />
						</div>
					) : (
						value?.map((v) => {
							const itemLabel = items?.find((i) => i?.id === v)?.label || v
							return (
								<div
									key={v}
									className="bg-gray-100 dark:bg-background-light-dark dark:border text-sm p-1 rounded-md flex items-center gap-1"
								>
									<span className={valueClassName}>
										{type === 'date'
											? dayjs(v).format('DD MMM, YYYY')
											: itemLabel}
									</span>
									<Tooltip
										label="Remove"
										className="w-[50px]"
										classNameChildrenWrapper="h-4"
									>
										<Close
											className="w-4 h-4 cursor-pointer text-gray-500 hover:text-gray-700"
											onClick={(e) => handleRemoveValue(v, e)}
										/>
									</Tooltip>
								</div>
							)
						})
					)}
					{!(loading && value?.length) && (
						<input
							ref={inputRef}
							type={type}
							className={cn(
								'w-auto min-w-[50px] box-border px-2 py-1 focus:outline-none rounded dark:text-text-main-dark bg-background-light hover:bg-background-light-hover dark:bg-transparent duration-default',
								'max-w-[100px]'
							)}
							placeholder={value.length === 0 ? placeholder : 'value...'}
							value={inputValue}
							onChange={(e) => {
								type === 'date'
									? handleDateChange(e)
									: setInputValue(e.target.value)
							}}
							onKeyDown={(e) => {
								if (!withoutKeyConfirm && type === 'text') {
									handleKeyDown(e)
								}
							}}
							onFocus={() => setIsOpen(true)}
							onBlur={() => setIsOpen(false)}
						/>
					)}
				</div>

				{isOpen && (
					<div
						className={cn(
							'absolute top-full -mt-1 left-0 right-0 bg-white dark:bg-background-dark border border-stroke-dark dark:border-stroke-light-dark rounded-b-md z-10 max-h-32 overflow-y-auto',
							itemsWrapperClassName
						)}
					>
						{loading ? (
							<div className="w-full h-10 flex items-center justify-center">
								<Loader size={15} className="" />
							</div>
						) : filteredItems?.length > 0 ? (
							filteredItems.map((item) => (
								<div
									key={item.id}
									className={cn(
										'px-3 py-2 hover:bg-background-light-hover dark:bg-background-dark dark:hover:bg-background-dark-hover cursor-pointer',
										itemClassName
									)}
									onMouseDown={(e) => e.preventDefault()}
									onClick={() => handleItemClick(item)}
								>
									{item.label}
								</div>
							))
						) : (
							<div className="p-2 text-center text-gray-400">No options</div>
						)}
					</div>
				)}
			</div>
			{error && <p className="mt-1 text-sm text-error">{error}</p>}
		</div>
	)
}

export default MultiValueInput

// import React, { FC, useState, useRef, ReactNode } from 'react'
// import { Close } from '@mui/icons-material'
// import cn from '../../lib/utils/cn'
// import dayjs from 'dayjs'
import { IconButton } from '@mui/material'
import Tooltip from './Tooltip'

// interface MultiValueInputProps {
// 	value?: string[]
// 	onChange: (value: string[]) => void
// 	label?: ReactNode
// 	placeholder?: string
// 	error?: string
// 	wrapperClassName?: string
// 	labelClassName?: string
// 	selectClassName?: string
// 	className?: string
// 	disabled?: boolean
// 	type?: 'text' | 'date'
// }

// const MultiValueInput: FC<MultiValueInputProps> = ({
// 	value = [],
// 	onChange,
// 	label = null,
// 	placeholder = 'Add values',
// 	error,
// 	wrapperClassName = '',
// 	labelClassName = '',
// 	selectClassName = '',
// 	className = '',
// 	disabled = false,
// 	type = 'text',
// }) => {
// 	const [inputValue, setInputValue] = useState('')
// 	const inputRef = useRef<HTMLInputElement>(null)

// 	const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
// 		if (e.key === 'Enter' && inputValue.trim()) {
// 			e.preventDefault()
// 			if (!value.includes(inputValue.trim())) {
// 				onChange([...value, inputValue.trim()])
// 			}
// 			setInputValue('')
// 		}
// 	}

// 	const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
// 		const date = e.target.value
// 		if (date && !value.includes(date)) {
// 			onChange([...value, date])
// 		}
// 		setInputValue('')
// 	}

// 	const handleRemoveValue = (valueToRemove: string, e: React.MouseEvent) => {
// 		e.stopPropagation()
// 		onChange(value.filter((v) => v !== valueToRemove))
// 	}

// 	return (
// 		<div
// 			className={cn(
// 				'flex flex-col w-full',
// 				disabled && 'opacity-60 pointer-events-none',
// 				wrapperClassName
// 			)}
// 		>
// 			{label && (
// 				<label
// 					className={cn(
// 						'text-text-main font-semibold text-base mb-2',
// 						labelClassName
// 					)}
// 				>
// 					{label}
// 				</label>
// 			)}
// 			<div className={cn('relative w-full', className)}>
// 				<div
// 					className={cn(
// 						'min-h-[36px] px-2 py-1 border border-stroke-dark bg-white dark:bg-background-dark rounded-md',
// 						'flex flex-wrap items-center gap-1',
// 						error && 'border-error',
// 						selectClassName
// 					)}
// 				>
// 					{value.map((v) => (
// 						<div
// 							key={v}
// 							className="bg-gray-100 text-sm px-2 py-0.5 rounded-md flex items-center gap-1"
// 						>
// 							<span>
// 								{type === 'date' ? dayjs(v).format('DD MMM, YYYY') : v}
// 							</span>
// 							<Close
// 								className="w-4 h-4 cursor-pointer text-gray-500 hover:text-gray-700"
// 								onClick={(e) => handleRemoveValue(v, e)}
// 							/>
// 						</div>
// 					))}
// 					<input
// 						ref={inputRef}
// 						type={type}
// 						className={cn(
// 							'flex-1 w-auto min-w-[50px] px-2 py-1 focus:outline-none rounded bg-background-light hover:bg-background-light-hover duration-default',
// 							'max-w-[100px]'
// 						)}
// 						placeholder={value.length === 0 ? placeholder : 'value...'}
// 						value={inputValue}
// 						onChange={
// 							type === 'date'
// 								? handleDateChange
// 								: (e) => setInputValue(e.target.value)
// 						}
// 						onKeyDown={type === 'text' ? handleKeyDown : undefined}
// 					/>
// 				</div>
// 			</div>
// 			{error && <p className="mt-1 text-sm text-error">{error}</p>}
// 		</div>
// 	)
// }

// export default MultiValueInput
